'use client'

import type { ColumnDefinition } from '@/components/atomic/table/types'

import { useMemo } from 'react'

import { convertMoodysRatingToNumber } from '@/utils/moodysRatings'
import { PLACEHOLDER } from '@/utils/presentation/formatting'
import { convertSpRatingToNumber } from '@/utils/spRatings'

import { useReferenceDataPermissions } from './use-reference-data-permissions'

/**
 * Hook that wraps column definitions and automatically updates rating column headers
 * based on organization permissions. This pre-computes headers for optimal performance.
 */
export const useRatingColumns = (
  columns: ColumnDefinition[],
): ColumnDefinition[] => {
  const { hasSPAccess, hasMoodysAccess, hasFitchAccess, isLoading } =
    useGetRatingsAccess()

  return useMemo(() => {
    if (isLoading) {
      return columns
    }

    // Update rating column headers based on permissions
    const updateColumns = (cols: ColumnDefinition[]): ColumnDefinition[] => {
      return cols.map((col) => {
        // Check if this is a rating column
        const isRatingColumn =
          col.cellRenderer?.name === 'RatingCellRenderer' ||
          col.cellRendererParams?.isRatingColumn === true ||
          col.colId === 'sp_rating' ||
          col.colId === 'average_sp_rating' ||
          col.field === 'sp_rating'

        // Check if this is an average rating column
        const isAverageRatingColumn =
          col.cellRendererParams?.isAverageRating === true

        if (isRatingColumn) {
          // Determine the appropriate header based on permissions
          let headerName = 'Rating'
          const prefix = isAverageRatingColumn ? 'Avg ' : ''

          const ratingParts: string[] = []
          if (hasMoodysAccess) ratingParts.push("Moody's")
          if (hasSPAccess) ratingParts.push('S&P')
          if (hasFitchAccess) ratingParts.push('Fitch')

          if (ratingParts.length > 0) {
            headerName = `${prefix}${ratingParts.join('/')}`
          } else if (isAverageRatingColumn) {
            headerName = 'Avg Rating'
          }

          let field: string | undefined
          let comparator:
            | ((valueA: string, valueB: string) => number)
            | undefined
          if (!isAverageRatingColumn) {
            if (hasSPAccess) {
              field = 'sp_rating'

              comparator = (valueA: string, valueB: string) => {
                const numericA = convertSpRatingToNumber(valueA)
                const numericB = convertSpRatingToNumber(valueB)
                return numericA - numericB
              }
            } else if (hasMoodysAccess) {
              field = 'moodys_rating'

              comparator = (valueA: string, valueB: string) => {
                const numericA = convertMoodysRatingToNumber(valueA)
                const numericB = convertMoodysRatingToNumber(valueB)
                return numericA - numericB
              }
            } else if (hasFitchAccess) {
              field = 'fitch_rating'

              // Fitch uses the same letter-based scale as S&P (AAA, AA+, etc.)
              comparator = (valueA: string, valueB: string) => {
                const numericA = convertSpRatingToNumber(valueA)
                const numericB = convertSpRatingToNumber(valueB)
                return numericA - numericB
              }
            }
          }

          // Compose the same agencies the header advertises into the export
          // value. Visible cells read `params.data` directly via
          // RatingCellRenderer and ignore valueFormatter, so this only affects
          // serialization (CSV export, etc.).
          // biome-ignore lint/suspicious/noExplicitAny: row shape varies
          const valueFormatter = (params: { data?: any }) => {
            const data = params.data
            if (!data) return ''
            const parts: string[] = []
            if (hasMoodysAccess) {
              parts.push(data.moodys_rating ?? data.moodysRating ?? '')
            }
            if (hasSPAccess) {
              parts.push(data.sp_rating ?? data.spRating ?? '')
            }
            if (hasFitchAccess) {
              parts.push(data.fitch_rating ?? '')
            }
            if (parts.every((p) => !p)) return ''
            return parts.join('/')
          }

          return {
            ...col,
            headerName,
            field,
            comparator,
            valueFormatter,
          }
        }

        return col
      })
    }

    return updateColumns(columns)
  }, [columns, hasSPAccess, hasMoodysAccess, hasFitchAccess, isLoading])
}

export const useGetRatingsAccess = (): {
  hasSPAccess: boolean | null
  hasMoodysAccess: boolean | null
  hasFitchAccess: boolean | null
  isLoading: boolean
} => {
  const { hasSPAccess, hasMoodysAccess, hasFitchAccess, isLoading } =
    useReferenceDataPermissions()

  // Stable reference — consumers spread this into useMemo deps.
  return useMemo(
    () => ({
      hasSPAccess,
      hasMoodysAccess,
      hasFitchAccess,
      isLoading,
    }),
    [hasSPAccess, hasMoodysAccess, hasFitchAccess, isLoading],
  )
}

/**
 * Get the appropriate rating label and value based on permissions and rating data
 * @param avgMoodysRating - The average Moody's rating value
 * @param avgSpRating - The average S&P rating value
 * @returns Object with label and value for display
 */
export const getRatingDisplay = (
  ratingsAccess: {
    hasSPAccess: boolean | null
    hasMoodysAccess: boolean | null
    isLoading: boolean
  },
  avgMoodysRating: string | null | undefined,
  avgSpRating: string | null | undefined,
): { label: string; value: string } => {
  // While loading, show generic label and placeholder
  if (ratingsAccess.isLoading) {
    return {
      label: 'Avg rating',
      value: PLACEHOLDER,
    }
  }

  // Both ratings access
  if (ratingsAccess.hasSPAccess && ratingsAccess.hasMoodysAccess) {
    return {
      label: 'Avg rating',
      value: `${avgMoodysRating || PLACEHOLDER} / ${avgSpRating || PLACEHOLDER}`,
    }
  }

  // S&P only
  if (ratingsAccess.hasSPAccess && !ratingsAccess.hasMoodysAccess) {
    return {
      label: 'Avg rating',
      value: avgSpRating || PLACEHOLDER,
    }
  }

  // Moody's only
  if (!ratingsAccess.hasSPAccess && ratingsAccess.hasMoodysAccess) {
    return {
      label: 'Avg rating',
      value: avgMoodysRating || PLACEHOLDER,
    }
  }

  // No access or undefined state
  return {
    label: 'Avg rating',
    value: PLACEHOLDER,
  }
}
